import type { ReactNode } from 'react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export interface KanbanColumn<T> {
  id: string
  label: string
  items: T[]
  /** Shown under the heading when the column is empty and that fact needs explaining. */
  emptyHint?: string
}

export interface KanbanBoardProps<T> {
  columns: KanbanColumn<T>[]
  /** Stable key for each card. */
  itemKey: (item: T) => string
  /** The card body. Keep it to a few lines — a board is for scanning, not reading. */
  renderCard: (item: T) => ReactNode
  /**
   * The per-card control that moves it. A `<Select>`, a menu, whatever the module needs —
   * the board does not know what moving means.
   */
  renderMove?: (item: T) => ReactNode
  /** Columns with nothing in them are shown by default; an empty column is information. */
  hideEmptyColumns?: boolean
  className?: string
  'aria-label'?: string
}

/**
 * Columns of cards, with an explicit control to move each one.
 *
 * <h2>No drag-and-drop, deliberately</h2>
 * Moving a candidate to "Offer" or a visa case to "Submitted" is a decision somebody made,
 * and it should look like one. A select records it as a choice, works with a keyboard and a
 * screen reader, cannot fire from a stray gesture on a trackpad, and gives the module a
 * natural place to refuse the move and say why — which a drop target does not.
 *
 * <h2>Empty columns stay</h2>
 * A stage nobody has reached is a real part of the process. Hiding it makes the pipeline look
 * shorter than the one being run, and hides the fact that everybody is stuck before it.
 * `hideEmptyColumns` exists for boards where the columns are a status enum rather than a
 * configured process.
 *
 * <p>Extracted from the PRO board, which had grown its own copy of this. Both now use it.
 */
export function KanbanBoard<T>({
  columns,
  itemKey,
  renderCard,
  renderMove,
  hideEmptyColumns = false,
  className,
  'aria-label': ariaLabel,
}: KanbanBoardProps<T>) {
  const visible = hideEmptyColumns ? columns.filter((column) => column.items.length > 0) : columns

  return (
    <div
      className={cn('grid gap-3 md:grid-cols-2 xl:grid-cols-3', className)}
      aria-label={ariaLabel}
    >
      {visible.map((column) => (
        <section key={column.id} aria-label={column.label} className="space-y-2">
          <h2 className="flex items-baseline justify-between text-sm font-semibold text-[var(--color-heading)]">
            {column.label}
            <span className="text-xs font-normal text-[var(--color-muted)]">
              {column.items.length}
            </span>
          </h2>

          {column.items.length === 0 && (
            <Card className="p-3">
              <p className="text-xs text-[var(--color-muted)]">
                {column.emptyHint ?? 'Nobody is here yet.'}
              </p>
            </Card>
          )}

          {column.items.map((item) => (
            <Card key={itemKey(item)} className="space-y-2 p-3">
              {renderCard(item)}
              {renderMove && <div className="space-y-1">{renderMove(item)}</div>}
            </Card>
          ))}
        </section>
      ))}
    </div>
  )
}
